import React, { useState } from "react";

export default function NewStarterPayrollForm({ onSubmit, initialData }) {
  const [formData, setFormData] = useState(
    initialData || {
      fullName: "",
      addressLine1: "",
      addressLine2: "",
      postcode: "",
      dob: "",
      niNumber: "",
      phone: "",
      email: "",
      starterDeclaration: "",
      hasStudentLoan: "",
      studentLoanPlan: "",
      hasPostgradLoan: "",
      rightToWorkShareCode: "",
      bankAccountName: "",
      bankName: "",
      sortCode: "",
      accountNumber: "",
      rollNumber: "",
      emergencyContactName: "",
      emergencyContactRelationship: "",
      emergencyContactPhone: "",
      hoursRestricted: "",
      hoursDetails: "",
      otherEmployment: "",
      otherEmploymentDetails: "",
      declarationAccepted: false,
    }
  );

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = "Required";
    if (!formData.dob) newErrors.dob = "Required";
    if (!formData.starterDeclaration)
      newErrors.starterDeclaration = "Please select one option";
    if (!formData.bankAccountName.trim())
      newErrors.bankAccountName = "Required";
    if (!formData.sortCode.trim()) newErrors.sortCode = "Required";
    if (!formData.accountNumber.trim()) newErrors.accountNumber = "Required";
    if (!formData.declarationAccepted)
      newErrors.declarationAccepted = "You must confirm the declaration";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (typeof onSubmit === "function") {
      onSubmit(formData);
    } else {
      console.log("New Starter Payroll Form data:", formData);
      alert("Form submitted (check console). Wire onSubmit to save to Firestore.");
    }
  };

  const sectionTitle = {
    fontWeight: 600,
    marginTop: 16,
    marginBottom: 8,
    fontSize: 14,
    color: "#111827",
  };

  const labelStyle = {
    fontSize: 13,
    color: "#374151",
    marginBottom: 4,
    display: "block",
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    fontSize: 13,
    outline: "none",
  };

  const errorStyle = {
    fontSize: 12,
    color: "#b91c1c",
    marginTop: 2,
  };

  const radioRow = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    marginRight: 16,
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
        New Starter Payroll Form
      </h3>
      <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>
        Please complete this form so we can set you up correctly on payroll in
        line with UK requirements.
      </p>

      {/* 1. Personal Details */}
      <div style={sectionTitle}>1. Personal Details</div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Full Name *</label>
        <input
          style={inputStyle}
          value={formData.fullName}
          onChange={(e) => handleChange("fullName", e.target.value)}
        />
        {errors.fullName && <div style={errorStyle}>{errors.fullName}</div>}
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Address Line 1</label>
        <input
          style={inputStyle}
          value={formData.addressLine1}
          onChange={(e) => handleChange("addressLine1", e.target.value)}
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Address Line 2</label>
        <input
          style={inputStyle}
          value={formData.addressLine2}
          onChange={(e) => handleChange("addressLine2", e.target.value)}
        />
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Postcode</label>
          <input
            style={inputStyle}
            value={formData.postcode}
            onChange={(e) => handleChange("postcode", e.target.value)}
          />
        </div>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Date of Birth *</label>
          <input
            type="date"
            style={inputStyle}
            value={formData.dob}
            onChange={(e) => handleChange("dob", e.target.value)}
          />
          {errors.dob && <div style={errorStyle}>{errors.dob}</div>}
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>National Insurance Number</label>
        <input
          style={inputStyle}
          value={formData.niNumber}
          onChange={(e) => handleChange("niNumber", e.target.value)}
        />
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Phone Number</label>
          <input
            style={inputStyle}
            value={formData.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
          />
        </div>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Email</label>
          <input
            style={inputStyle}
            type="email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
          />
        </div>
      </div>

      {/* 2. Starter Declaration */}
      <div style={sectionTitle}>2. Starter Declaration (HMRC)</div>
      <p style={{ fontSize: 12, color: "#4b5563", marginBottom: 4 }}>
        Please select the statement that applies to you. This is required for
        your tax code if we do not have a P45.
      </p>

      <div style={{ marginBottom: 6 }}>
        <label style={radioRow}>
          <input
            type="radio"
            name="starterDecl"
            value="A"
            checked={formData.starterDeclaration === "A"}
            onChange={(e) =>
              handleChange("starterDeclaration", e.target.value)
            }
          />
          <span style={{ fontSize: 12 }}>
            A – This is my first job since 6 April and I have not received
            taxable state benefits.
          </span>
        </label>
      </div>
      <div style={{ marginBottom: 6 }}>
        <label style={radioRow}>
          <input
            type="radio"
            name="starterDecl"
            value="B"
            checked={formData.starterDeclaration === "B"}
            onChange={(e) =>
              handleChange("starterDeclaration", e.target.value)
            }
          />
          <span style={{ fontSize: 12 }}>
            B – This is not my first job since 6 April or I receive a state
            benefit.
          </span>
        </label>
      </div>
      <div style={{ marginBottom: 6 }}>
        <label style={radioRow}>
          <input
            type="radio"
            name="starterDecl"
            value="C"
            checked={formData.starterDeclaration === "C"}
            onChange={(e) =>
              handleChange("starterDeclaration", e.target.value)
            }
          />
          <span style={{ fontSize: 12 }}>
            C – I have another job or I receive a pension.
          </span>
        </label>
      </div>
      {errors.starterDeclaration && (
        <div style={errorStyle}>{errors.starterDeclaration}</div>
      )}

      {/* 3. Student Loans */}
      <div style={sectionTitle}>3. Student Loan</div>

      <div style={{ marginBottom: 8 }}>
        <span style={labelStyle}>Do you have a student loan?</span>
        <div style={{ display: "flex", marginBottom: 4 }}>
          <label style={radioRow}>
            <input
              type="radio"
              name="hasStudentLoan"
              value="yes"
              checked={formData.hasStudentLoan === "yes"}
              onChange={(e) => handleChange("hasStudentLoan", e.target.value)}
            />
            Yes
          </label>
          <label style={radioRow}>
            <input
              type="radio"
              name="hasStudentLoan"
              value="no"
              checked={formData.hasStudentLoan === "no"}
              onChange={(e) => handleChange("hasStudentLoan", e.target.value)}
            />
            No
          </label>
        </div>
      </div>

      {formData.hasStudentLoan === "yes" && (
        <>
          <div style={{ marginBottom: 8 }}>
            <span style={labelStyle}>Student loan plan type</span>
            <select
              style={inputStyle}
              value={formData.studentLoanPlan}
              onChange={(e) =>
                handleChange("studentLoanPlan", e.target.value)
              }
            >
              <option value="">Select...</option>
              <option value="Plan 1">Plan 1</option>
              <option value="Plan 2">Plan 2</option>
              <option value="Plan 4">Plan 4</option>
              <option value="Postgraduate">Postgraduate Loan</option>
            </select>
          </div>
          <div style={{ marginBottom: 8 }}>
            <span style={labelStyle}>Do you have a Postgraduate Loan?</span>
            <div style={{ display: "flex" }}>
              <label style={radioRow}>
                <input
                  type="radio"
                  name="hasPostgradLoan"
                  value="yes"
                  checked={formData.hasPostgradLoan === "yes"}
                  onChange={(e) =>
                    handleChange("hasPostgradLoan", e.target.value)
                  }
                />
                Yes
              </label>
              <label style={radioRow}>
                <input
                  type="radio"
                  name="hasPostgradLoan"
                  value="no"
                  checked={formData.hasPostgradLoan === "no"}
                  onChange={(e) =>
                    handleChange("hasPostgradLoan", e.target.value)
                  }
                />
                No
              </label>
            </div>
          </div>
        </>
      )}

      {/* 4. Right to Work */}
      <div style={sectionTitle}>4. Right to Work in the UK</div>
      <p style={{ fontSize: 12, color: "#4b5563", marginBottom: 4 }}>
        You will also need to provide original documents to evidence your right
        to work. If using an online share code, enter it below:
      </p>
      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Share Code (if applicable)</label>
        <input
          style={inputStyle}
          value={formData.rightToWorkShareCode}
          onChange={(e) =>
            handleChange("rightToWorkShareCode", e.target.value)
          }
        />
      </div>

      {/* 5. Bank Details */}
      <div style={sectionTitle}>5. Bank Details (for salary payments)</div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Account Holder Name *</label>
        <input
          style={inputStyle}
          value={formData.bankAccountName}
          onChange={(e) =>
            handleChange("bankAccountName", e.target.value)
          }
        />
        {errors.bankAccountName && (
          <div style={errorStyle}>{errors.bankAccountName}</div>
        )}
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Bank Name</label>
        <input
          style={inputStyle}
          value={formData.bankName}
          onChange={(e) => handleChange("bankName", e.target.value)}
        />
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Sort Code (6 digits) *</label>
          <input
            style={inputStyle}
            value={formData.sortCode}
            onChange={(e) => handleChange("sortCode", e.target.value)}
          />
          {errors.sortCode && (
            <div style={errorStyle}>{errors.sortCode}</div>
          )}
        </div>
        <div style={{ flex: 1, marginBottom: 10 }}>
          <label style={labelStyle}>Account Number (8 digits) *</label>
          <input
            style={inputStyle}
            value={formData.accountNumber}
            onChange={(e) =>
              handleChange("accountNumber", e.target.value)
            }
          />
          {errors.accountNumber && (
            <div style={errorStyle}>{errors.accountNumber}</div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Roll Number (if Building Society)</label>
        <input
          style={inputStyle}
          value={formData.rollNumber}
          onChange={(e) => handleChange("rollNumber", e.target.value)}
        />
      </div>

      {/* 6. Emergency Contact */}
      <div style={sectionTitle}>6. Emergency Contact</div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Full Name</label>
        <input
          style={inputStyle}
          value={formData.emergencyContactName}
          onChange={(e) =>
            handleChange("emergencyContactName", e.target.value)
          }
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Relationship</label>
        <input
          style={inputStyle}
          value={formData.emergencyContactRelationship}
          onChange={(e) =>
            handleChange(
              "emergencyContactRelationship",
              e.target.value
            )
          }
        />
      </div>

      <div style={{ marginBottom: 10 }}>
        <label style={labelStyle}>Phone Number</label>
        <input
          style={inputStyle}
          value={formData.emergencyContactPhone}
          onChange={(e) =>
            handleChange("emergencyContactPhone", e.target.value)
          }
        />
      </div>

      {/* 7. Working Time & Other Employment */}
      <div style={sectionTitle}>7. Working Time & Other Employment</div>

      <div style={{ marginBottom: 8 }}>
        <span style={labelStyle}>
          Do you have any restriction on the number of hours you may work?
        </span>
        <div style={{ display: "flex" }}>
          <label style={radioRow}>
            <input
              type="radio"
              name="hoursRestricted"
              value="yes"
              checked={formData.hoursRestricted === "yes"}
              onChange={(e) =>
                handleChange("hoursRestricted", e.target.value)
              }
            />
            Yes
          </label>
          <label style={radioRow}>
            <input
              type="radio"
              name="hoursRestricted"
              value="no"
              checked={formData.hoursRestricted === "no"}
              onChange={(e) =>
                handleChange("hoursRestricted", e.target.value)
              }
            />
            No
          </label>
        </div>
      </div>

      {formData.hoursRestricted === "yes" && (
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Please give details</label>
          <textarea
            style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
            value={formData.hoursDetails}
            onChange={(e) => handleChange("hoursDetails", e.target.value)}
          />
        </div>
      )}

      <div style={{ marginBottom: 8 }}>
        <span style={labelStyle}>
          Are you employed by another employer or agency?
        </span>
        <div style={{ display: "flex" }}>
          <label style={radioRow}>
            <input
              type="radio"
              name="otherEmployment"
              value="yes"
              checked={formData.otherEmployment === "yes"}
              onChange={(e) =>
                handleChange("otherEmployment", e.target.value)
              }
            />
            Yes
          </label>
          <label style={radioRow}>
            <input
              type="radio"
              name="otherEmployment"
              value="no"
              checked={formData.otherEmployment === "no"}
              onChange={(e) =>
                handleChange("otherEmployment", e.target.value)
              }
            />
            No
          </label>
        </div>
      </div>

      {formData.otherEmployment === "yes" && (
        <div style={{ marginBottom: 10 }}>
          <label style={labelStyle}>Please give details</label>
          <textarea
            style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
            value={formData.otherEmploymentDetails}
            onChange={(e) =>
              handleChange("otherEmploymentDetails", e.target.value)
            }
          />
        </div>
      )}

      {/* 8. Declaration */}
      <div style={sectionTitle}>8. Declaration</div>
      <p style={{ fontSize: 12, color: "#4b5563", marginBottom: 8 }}>
        I confirm that the information I have provided is true and correct to
        the best of my knowledge. I understand that giving false information may
        affect my tax code, pay and employment status.
      </p>
      <label style={{ ...radioRow, marginBottom: 4 }}>
        <input
          type="checkbox"
          checked={formData.declarationAccepted}
          onChange={(e) =>
            handleChange("declarationAccepted", e.target.checked)
          }
        />
        <span style={{ fontSize: 12 }}>I confirm the above declaration *</span>
      </label>
      {errors.declarationAccepted && (
        <div style={errorStyle}>{errors.declarationAccepted}</div>
      )}

      <button
        type="submit"
        style={{
          marginTop: 16,
          width: "100%",
          padding: "10px 0",
          borderRadius: 9999,
          border: "none",
          fontSize: 14,
          fontWeight: 600,
          backgroundColor: "#0284c7",
          color: "white",
          cursor: "pointer",
        }}
      >
        Submit Form
      </button>
    </form>
  );
}
